import { yupResolver } from '@hookform/resolvers/yup';
import {
    Box,
    Card,
    CardActions,
    CardContent,
    Checkbox,
    FormControl,
    FormControlLabel,
    FormHelperText,
    FormLabel,
    Radio,
    RadioGroup,
    Typography,
    styled,
} from '@mui/material';
import useProductsPriceSWR, {
    GetCustomPricesResponse,
} from 'hooks/products/useProductsPriceSWR';
import React, { useEffect, useMemo } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { formatPrice } from 'utils/numberFormatter';
import * as yup from 'yup';
import useShopifyCart from '../../../hooks/shopify/useShopifyCart';
import useShopifyCustomer from '../../../hooks/shopify/useShopifyCustomer';
import { CartLineItem, CartResponse } from '../../../models/Cart';
import storageService, { Locals } from '../../../utils/storageService';
import { getCurrency, isNullOrUndefined } from '../../../utils/utils';
import PrimaryBtn from '../../shared/Buttons/PrimaryButton';
import { CartActions } from './CartActions';

const schema = yup.object().shape({
    terms_of_service: yup.boolean().oneOf([true], 'REQUIRED'),
    statement: yup.boolean().oneOf([true], 'REQUIRED'),
});

const StyledPriceInfo = styled(Typography)(() => ({
    color: '#868686',
    paddingTop: '10px',
}));

export const CartSummaries = (props: {
    cart: CartResponse;
    customPrices: GetCustomPricesResponse;
    cartItems?: CartLineItem[];
}) => {
    const { cart, customPrices, cartItems } = props;
    const { t } = useTranslation('cart', {
        keyPrefix: 'CART',
    });
    const token = storageService.getItem(Locals.SHOPIFY_ACCESS_TOKEN);
    const {
        register,
        handleSubmit,
        formState: { errors },
        watch,
    } = useForm<FormValues>({
        resolver: yupResolver(schema),
    });
    const shopifyCart = useShopifyCart();
    const { customer, isLoading, isError, isValidating } =
        useShopifyCustomer().getCustomerSWR(token);
    const [value, setValue] = React.useState(
        cart.attributes.find((elem) => elem.key == 'payment_method').value
    );
    const [isBanking, setIsBanking] = React.useState(false);

    const { getMinNetPrice } = useProductsPriceSWR();

    const redirect = () => {
        window.location.replace(cart.checkoutUrl + '?step=contact_information');
    };
    const goToCheckout: SubmitHandler<FormValues> = (data) => {
        if (isBanking && !isNullOrUndefined(data.payment_method)) {
            ///update
            shopifyCart
                .updateAttributes([
                    {
                        key: 'payment_method',
                        value: data.payment_method,
                    },
                ])
                .then(() => {
                    redirect();
                });
        } else {
            redirect();
        }
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setValue((event.target as HTMLInputElement).value);
    };

    useEffect(() => {
        if (!isNullOrUndefined(customer)) {
            const metafield = customer.metafields.find(
                (elem) => elem.key == 'bc_payment_method_code'
            );
            if (metafield) {
                if (metafield.value == 'BANK') {
                    setIsBanking(true);
                } else {
                    setValue(metafield.value);
                }
            } else {
                setValue('UTANVET');
            }
        }
    }, [customer, isLoading, isValidating]);

    const totalAmount = useMemo((): number => {
        if (!customPrices || Object.keys(customPrices).length === 0) {
            return Number(cart.cost.totalAmount.amount);
        }

        let total = 0;

        for (const item of cartItems) {
            const minCustomPrice = getMinNetPrice(item);
            const subtotal = minCustomPrice * item.quantity;
            total += subtotal;
        }

        return total;
    }, [cart.cost.totalAmount.amount, cartItems, customPrices]);

    return (
        <Card>
            <form onSubmit={handleSubmit(goToCheckout)}>
                <CardContent>
                    <Typography
                        variant={'h2'}
                        sx={{
                            borderBottom: '1px solid black',
                            paddingBottom: '10px',
                            fontWeight: 'normal',
                        }}
                    >
                        {t('ORDER_SUMMARY')}
                    </Typography>
                    <Box
                        component={'span'}
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            paddingY: '15px',
                            borderBottom: '1px solid #868686',
                        }}
                    >
                        <Typography
                            variant={'h3'}
                            sx={{
                                fontWeight: 'normal',
                            }}
                        >
                            {t('TOTAL')}:
                        </Typography>
                        <Typography
                            variant={'h3'}
                            sx={{ fontWeight: 'normal' }}
                        >
                            {formatPrice(totalAmount)}
                            {getCurrency(cart.cost.totalAmount.currencyCode)}
                        </Typography>
                    </Box>
                    <StyledPriceInfo>{t('PRICE_INFO')}</StyledPriceInfo>
                    <Typography sx={{ paddingBlock: '15px' }}>
                        {t('PAYING_INFO')}
                    </Typography>

                    <FormControl fullWidth>
                        <FormControlLabel
                            control={<Checkbox {...register('statement')} />}
                            label={
                                <Typography>
                                    {t('STATEMENT').toString()}
                                </Typography>
                            }
                        />
                        <FormHelperText error={!!errors.statement}>
                            {errors.statement && t(errors.statement.message)}
                        </FormHelperText>
                    </FormControl>
                    {isBanking && (
                        <FormControl sx={{ paddingTop: '10px' }}>
                            <FormLabel id="demo-controlled-radio-buttons-group">
                                {t('CHOOSE_PAYMENT_METHOD')}
                            </FormLabel>
                            <RadioGroup
                                aria-labelledby="demo-controlled-radio-buttons-group"
                                name="controlled-radio-buttons-group"
                                value={value}
                                onChange={handleChange}
                            >
                                <FormControlLabel
                                    value="UTANVET"
                                    control={
                                        <Radio
                                            {...register('payment_method')}
                                        />
                                    }
                                    label={t('CASH')}
                                />
                                <FormControlLabel
                                    value="BANK"
                                    control={
                                        <Radio
                                            {...register('payment_method')}
                                        />
                                    }
                                    label={t('BANK')}
                                />
                            </RadioGroup>
                        </FormControl>
                    )}
                </CardContent>
                <CardActions
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '10px',
                    }}
                >
                    <PrimaryBtn
                        type="submit"
                        sx={{ flex: '1', width: '100%' }}
                        disabled={
                            Number(cart.cost.totalAmount.amount) == 0 ||
                            isLoading ||
                            !watch('statement')
                        }
                    >
                        {t('ORDER_BUTTON')}
                    </PrimaryBtn>
                    <CartActions
                        products={cart.lines.edges}
                        disabled={!watch('statement')}
                    />
                </CardActions>
            </form>
        </Card>
    );
};

type FormValues = {
    terms_of_service: boolean;
    statement: boolean;
    payment_method: string;
};
